import { useMutation } from "@apollo/client";
import { parseError } from "apollo";
import Button from "components/base/Button";
import { Alert } from "components/base/Toast";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import ForecastProductView from "../ForecastProductView";
import { FORECAST_CATEGORY_SAVE_SUBMISSION } from "./graphql";

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function normalizeProductId(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  if (/^\d+$/.test(raw)) return String(parseInt(raw, 10));
  return raw;
}

const ConfirmSubmitView = ({ category, payload, hide }) => {
  const [hasConfirmed, setHasConfirmed] = useState(false);
  const navigate = useNavigate();

  const months = (Array.isArray(payload?.columns) ? payload.columns : [])
    .map((month) => String(month || "").trim())
    .filter(Boolean);

  const products = Array.isArray(category?.products) ? category.products : [];
  const productsById = products.reduce((acc, product) => {
    acc[normalizeProductId(product.id)] = product;
    return acc;
  }, {});

  const items = Array.isArray(payload?.items) ? payload.items : [];
  const rows = items
    .map((item, index) => {
      const productId = normalizeProductId(item?.wis_product_id);
      const fcst = item?.fcst || {};
      const hasForecastQty = months.some(
        (month) => toNumber(fcst?.[month]) > 0,
      );
      if (!hasForecastQty) return null;
      return {
        key: `${productId || "unknown"}-${index}`,
        productId,
        product: productsById[productId],
        fcst,
      };
    })
    .filter(Boolean);

  const [submit, submitRes] = useMutation(FORECAST_CATEGORY_SAVE_SUBMISSION, {
    onCompleted(res) {
      const submissionId = res?.forecastCategorySaveSubmission?.submission?.id;
      Alert("success", "Forecast submitted successfully.");
      if (submissionId) {
        navigate(
          `/sales/forecast/${category.mode}/categories/${category.id}/submissions/${submissionId}`,
        );
        return;
      }
      if (typeof hide === "function") hide();
    },
    onError(error) {
      Alert("error", parseError(error));
    },
  });

  const submitHandler = () => {
    submit({
      variables: {
        forecastCategoryId: category.id,
        data: JSON.stringify(payload || {}),
        submittedAt: new Date().toISOString(),
      },
    });
  };

  return (
    <div className="space-y-4">
      <div className="opacity-80">
        Please review the products and forecast quantities below before
        submitting.
      </div>

      <div className="max-h-[60vh] overflow-auto border border-gray-200 dark:border-gray-700 rounded">
        <table className="w-full text-xs">
          <thead className="sticky top-0 z-10 backdrop-blur bg-gray-200 bg-opacity-80 dark:bg-gray-800 dark:bg-opacity-80">
            <tr className="border-b border-gray-200 dark:border-gray-700">
              <th className="px-4 py-3 text-left whitespace-nowrap">
                Product ({rows.length})
              </th>
              {months.map((month) => (
                <th
                  key={month}
                  className="px-2 py-3 text-right whitespace-nowrap border-x border-gray-200 dark:border-gray-700"
                >
                  {month}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={1 + months.length}
                  className="px-4 py-6 text-center text-gray-500"
                >
                  No products with forecast quantity.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={row.key}
                  className="border-y border-gray-100 dark:border-gray-700"
                >
                  <td className="px-4 py-2">
                    {row.product ? (
                      <ForecastProductView product={row.product} />
                    ) : (
                      <span className="opacity-70">
                        Unknown product (ID: {row.productId || "N/A"})
                      </span>
                    )}
                  </td>
                  {months.map((month) => (
                    <td
                      key={`${row.key}-${month}`}
                      className="px-2 py-2 text-right whitespace-nowrap border-x border-gray-100 dark:border-gray-700"
                    >
                      {toNumber(row.fcst?.[month]).toLocaleString()}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex space-x-2 items-center">
        <input
          id="confirm-submit-forecast"
          type="checkbox"
          checked={hasConfirmed}
          onChange={(e) => setHasConfirmed(e.target.checked)}
        />
        <label htmlFor="confirm-submit-forecast" className="leading-relaxed">
          I confirm that I have reviewed all forecast quantities above, and I
          understand this submission will be shared for production and inventory
          planning.
        </label>
      </div>

      <div className="flex items-center space-x-4">
        <Button
          primary
          onClick={submitHandler}
          loading={submitRes.loading}
          disabled={!hasConfirmed}
        >
          Submit Forecast
        </Button>

        {!hasConfirmed && (
          <div className="text-sm text-orange-600 font-semibold">
            Please confirm before submitting the forecast.
          </div>
        )}
      </div>
    </div>
  );
};

export default ConfirmSubmitView;
